import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FormEvent,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type {
  Checkpoint,
  CheckpointParameter,
} from '@/domains/checkpoint/checkpoint.types';
import {
  createSiteComplianceDraftConfiguration,
  filterSiteComplianceCheckpoints,
  getLatestSiteComplianceConfiguration,
  getPreviousSiteComplianceConfiguration,
  getSiteComplianceCheckpointRowKey,
  getSiteComplianceCheckpointsCountWithInvalidParameters,
  getSortedSiteComplianceConfigurations,
  mapSiteComplianceCheckpointsToOnboardingViewModels,
  updateSiteComplianceCheckpointParameters,
  useSiteConfigurationQuery,
  useSiteDetailQuery,
  useSitesQuery,
  type SiteComplianceConfiguration,
} from '@/domains/siteCompliance';
import type {
  OnboardingCheckpointListProps,
  OnboardingCheckpointSearchBarProps,
  OnboardingConfigurationStatusPanelProps,
  OnboardingFlowHeaderProps,
} from '@/features/onboarding/types/onboarding.types';
import { getCheckpointVersion } from '@/lib/checkpointKeys';
import { getCurrentUserPrincipal } from '@/lib/currentUser';
import {
  useMobileRouteLoading,
  useResponsiveViewport,
} from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';

import {
  buildSiteComplianceDetailsPath,
  buildSiteComplianceListPath,
  buildSiteComplianceOnboardPath,
} from '../../siteComplianceRouting';
import SiteComplianceCheckpointConfigDrawer from './SiteComplianceCheckpointConfigDrawer';
import SiteComplianceOnboardConfigureForm from './SiteComplianceOnboardConfigureForm';
import SiteComplianceOnboardDetails from './SiteComplianceOnboardDetails';

type SiteConfigurationMap = Record<string, SiteComplianceConfiguration[]>;
type CheckpointListProps = OnboardingCheckpointListProps<Checkpoint>;

interface UseSiteComplianceOnboardControllerResult {
  checkpointConfigProps: ComponentProps<
    typeof SiteComplianceCheckpointConfigDrawer
  >;
  checkpointListProps: CheckpointListProps;
  checkpointSearchProps: OnboardingCheckpointSearchBarProps | null;
  configureFormProps: ComponentProps<
    typeof SiteComplianceOnboardConfigureForm
  >;
  detailsProps: ComponentProps<typeof SiteComplianceOnboardDetails>;
  emptyStateMessage: string | null;
  hasLatestConfiguration: boolean;
  headerProps: OnboardingFlowHeaderProps;
  isMobileViewport: boolean;
  statusProps: OnboardingConfigurationStatusPanelProps | null;
}

export const useSiteComplianceOnboardController =
  (): UseSiteComplianceOnboardControllerResult => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const selectedSiteId = searchParams.get('siteId') ?? '';
    const isMobileViewport = useResponsiveViewport();
    const [checkpointSearch, setCheckpointSearch] = useState('');
    const [siteSearch, setSiteSearch] = useState('');
    const [activeCheckpointKey, setActiveCheckpointKey] =
      useState<string | null>(null);
    const [configurationsBySite, setConfigurationsBySite] =
      useState<SiteConfigurationMap>({});

    const showToast = useToast((store) => store.showToast);
    const sitesQuery = useSitesQuery({
      pageSize: 50,
      search: siteSearch || undefined,
    });
    const siteDetailQuery = useSiteDetailQuery(selectedSiteId);
    const siteConfigurationQuery = useSiteConfigurationQuery(selectedSiteId);

    useMobileRouteLoading(
      sitesQuery.isLoading ||
        sitesQuery.isFetching ||
        siteDetailQuery.isLoading ||
        siteDetailQuery.isFetching ||
        siteConfigurationQuery.isLoading ||
        siteConfigurationQuery.isFetching,
    );

    const siteOptions = useMemo(
      () => {
        const optionsById = new Map(
          (sitesQuery.data?.items ?? []).map((site) => [site.siteId, site]),
        );
        const selectedDetailSite = siteDetailQuery.data?.site;

        if (selectedDetailSite) {
          optionsById.set(selectedDetailSite.siteId, selectedDetailSite);
        }

        return Array.from(optionsById.values()).sort((left, right) =>
          left.siteId.localeCompare(right.siteId),
        );
      },
      [siteDetailQuery.data?.site, sitesQuery.data?.items],
    );

    const selectedSite = useMemo(
      () =>
        siteDetailQuery.data?.site ??
        siteOptions.find((site) => site.siteId === selectedSiteId) ??
        null,
      [selectedSiteId, siteDetailQuery.data?.site, siteOptions],
    );

    useEffect(() => {
      if (!selectedSite || siteConfigurationQuery.isLoading) {
        return;
      }

      setConfigurationsBySite((currentConfigurationsBySite) => {
        if (currentConfigurationsBySite[selectedSite.siteId]) {
          return currentConfigurationsBySite;
        }

        const configurations = siteConfigurationQuery.data?.configurations ?? [];

        return {
          ...currentConfigurationsBySite,
          [selectedSite.siteId]: configurations,
        };
      });
    }, [
      selectedSite,
      siteConfigurationQuery.data?.configurations,
      siteConfigurationQuery.isLoading,
    ]);

    const siteConfigurations = useMemo(
      () =>
        selectedSite
          ? configurationsBySite[selectedSite.siteId] ?? []
          : [],
      [configurationsBySite, selectedSite],
    );

    const sortedConfigurations = useMemo(
      () => getSortedSiteComplianceConfigurations(siteConfigurations),
      [siteConfigurations],
    );

    const latestConfiguration = useMemo(
      () => getLatestSiteComplianceConfiguration(sortedConfigurations),
      [sortedConfigurations],
    );

    const previousConfiguration = useMemo(
      () => getPreviousSiteComplianceConfiguration(sortedConfigurations),
      [sortedConfigurations],
    );

    useEffect(() => {
      setCheckpointSearch('');
      setActiveCheckpointKey(null);
    }, [latestConfiguration?.configurationId, selectedSiteId]);

    const filteredCheckpoints = useMemo(
      () =>
        filterSiteComplianceCheckpoints({
          checkpointSearch,
          checkpoints: latestConfiguration?.checkpoints ?? [],
          previousConfiguration,
        }),
      [checkpointSearch, latestConfiguration, previousConfiguration],
    );

    const checkpointsCountWithInvalidParameters = useMemo(
      () =>
        getSiteComplianceCheckpointsCountWithInvalidParameters(
          latestConfiguration,
        ),
      [latestConfiguration],
    );

    const activeCheckpoint = useMemo(() => {
      if (!activeCheckpointKey || !latestConfiguration) {
        return null;
      }

      return (
        latestConfiguration.checkpoints.find(
          (checkpoint) =>
            getSiteComplianceCheckpointRowKey(checkpoint) ===
            activeCheckpointKey,
        ) ?? null
      );
    }, [activeCheckpointKey, latestConfiguration]);

    const handleBack = useCallback(() => {
      if (selectedSiteId) {
        navigate(
          buildSiteComplianceDetailsPath(location.pathname, selectedSiteId),
        );
        return;
      }

      navigate(buildSiteComplianceListPath(location.pathname));
    }, [location.pathname, navigate, selectedSiteId]);

    const handleSelectedSiteChange = useCallback(
      (siteId: string) => {
        setSiteSearch('');

        if (!siteId) {
          navigate(buildSiteComplianceOnboardPath(location.pathname));
          return;
        }

        navigate(buildSiteComplianceOnboardPath(location.pathname, siteId));
      },
      [location.pathname, navigate],
    );

    const handleConfigureSubmit = useCallback(
      (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!selectedSite) {
          return;
        }

        setConfigurationsBySite((currentConfigurationsBySite) => {
          const currentConfigurations =
            currentConfigurationsBySite[selectedSite.siteId] ??
            [];
          const currentLatestConfiguration =
            getLatestSiteComplianceConfiguration(
              getSortedSiteComplianceConfigurations(currentConfigurations),
            );

          if (currentLatestConfiguration?.isDraft) {
            return currentConfigurationsBySite;
          }

          const draftConfiguration = createSiteComplianceDraftConfiguration({
            configuration: currentLatestConfiguration,
            siteId: selectedSite.siteId,
          });

          return {
            ...currentConfigurationsBySite,
            [selectedSite.siteId]: [
              draftConfiguration,
              ...currentConfigurations,
            ],
          };
        });

        showToast('success', {
          message: 'Draft site configuration created successfully',
        });
      },
      [selectedSite, showToast],
    );

    const handleSaveConfiguration = useCallback(() => {
      if (!selectedSite || !latestConfiguration?.isDraft) {
        return;
      }

      if (checkpointsCountWithInvalidParameters > 0) {
        showToast('warning', {
          message:
            'Complete the required checkpoint parameters before publishing this configuration.',
        });
        return;
      }

      const savedAt = new Date().toISOString();
      const savedConfigurationId = `site-config-${selectedSite.siteId}-${Date.now()}`;

      setConfigurationsBySite((currentConfigurationsBySite) => ({
        ...currentConfigurationsBySite,
        [selectedSite.siteId]: (
          currentConfigurationsBySite[selectedSite.siteId] ?? []
        ).map((configuration) =>
          configuration.configurationId === latestConfiguration.configurationId
            ? {
                ...configuration,
                isDraft: false,
                configurationId: savedConfigurationId,
                createdAt: savedAt,
                updatedAt: savedAt,
                createdBy: getCurrentUserPrincipal(),
              }
            : configuration,
        ),
      }));

      showToast('success', {
        message: 'Site configuration saved successfully',
      });
    }, [
      checkpointsCountWithInvalidParameters,
      latestConfiguration,
      selectedSite,
      showToast,
    ]);

    const handleCancelConfiguration = useCallback(() => {
      if (!selectedSite || !latestConfiguration?.isDraft) {
        return;
      }

      setConfigurationsBySite((currentConfigurationsBySite) => ({
        ...currentConfigurationsBySite,
        [selectedSite.siteId]: (
          currentConfigurationsBySite[selectedSite.siteId] ?? []
        ).filter(
          (configuration) =>
            configuration.configurationId !== latestConfiguration.configurationId,
        ),
      }));

      setActiveCheckpointKey(null);

      showToast('info', {
        message: 'Draft site configuration discarded',
      });
    }, [latestConfiguration, selectedSite, showToast]);

    const handleConfigureCheckpoint = useCallback((checkpoint: Checkpoint) => {
      setActiveCheckpointKey(getSiteComplianceCheckpointRowKey(checkpoint));
    }, []);

    const handleCheckpointSearchChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setCheckpointSearch(event.target.value);
      },
      [],
    );

    const handleSiteSearchChange = useCallback((value: string) => {
      setSiteSearch(value);
    }, []);

    const handleCloseCheckpointConfig = useCallback(() => {
      setActiveCheckpointKey(null);
    }, []);

    const handleSaveCheckpointParameters = useCallback(
      (parameters: CheckpointParameter[]) => {
        if (!selectedSite || !latestConfiguration?.isDraft || !activeCheckpoint) {
          return;
        }

        setConfigurationsBySite((currentConfigurationsBySite) => ({
          ...currentConfigurationsBySite,
          [selectedSite.siteId]: (
            currentConfigurationsBySite[selectedSite.siteId] ?? []
          ).map((configuration) =>
            configuration.configurationId ===
            latestConfiguration.configurationId
              ? updateSiteComplianceCheckpointParameters({
                  checkpointId: activeCheckpoint.id,
                  checkpointVersion: getCheckpointVersion(activeCheckpoint),
                  configuration,
                  parameters,
                })
              : configuration,
          ),
        }));

        showToast('success', {
          message: 'Checkpoint parameters updated successfully',
        });
      },
      [activeCheckpoint, latestConfiguration, selectedSite, showToast],
    );

    const checkpointListProps = useMemo<CheckpointListProps>(
      () => ({
        checkpoints: mapSiteComplianceCheckpointsToOnboardingViewModels(
          filteredCheckpoints,
          previousConfiguration,
        ),
        onConfigureCheckpoint: handleConfigureCheckpoint,
      }),
      [
        filteredCheckpoints,
        handleConfigureCheckpoint,
        previousConfiguration,
      ],
    );

    const hasLatestConfiguration = Boolean(selectedSite && latestConfiguration);
    const emptyStateMessage =
      hasLatestConfiguration && filteredCheckpoints.length === 0
        ? 'No checkpoints match the current search.'
        : null;

    return {
      checkpointConfigProps: {
        checkpoint: activeCheckpoint,
        configurationId: latestConfiguration?.configurationId ?? '',
        isOpen: Boolean(activeCheckpoint),
        isReadOnly: !latestConfiguration?.isDraft,
        onClose: handleCloseCheckpointConfig,
        onSaveParameters: handleSaveCheckpointParameters,
      },
      checkpointListProps,
      checkpointSearchProps:
        hasLatestConfiguration && latestConfiguration
          ? {
              filteredCount: filteredCheckpoints.length,
              inputId: 'site-compliance-checkpoint-search',
              label: 'Search checkpoints',
              onSearchChange: handleCheckpointSearchChange,
              placeholder:
                'Search by id, description, category, accountable, or status',
              resultsLabel: 'checkpoints',
              searchValue: checkpointSearch,
              totalCount: latestConfiguration.checkpoints.length,
            }
          : null,
      configureFormProps: {
        isLoadingSites:
          sitesQuery.isLoading ||
          siteDetailQuery.isLoading,
        latestConfiguration,
        onSiteSearchChange: handleSiteSearchChange,
        onSelectedSiteChange: handleSelectedSiteChange,
        onSubmit: handleConfigureSubmit,
        selectedSiteId,
        sites: siteOptions,
      },
      detailsProps: {
        latestConfiguration,
        site: selectedSite,
      },
      emptyStateMessage,
      hasLatestConfiguration,
      headerProps: {
        onBack: handleBack,
        title: 'Configure Site',
      },
      isMobileViewport,
      statusProps:
        latestConfiguration && selectedSite
          ? {
              draftMessage: `Configuration is in draft mode for ${selectedSite.siteId}. Review the network checkpoints, complete any required parameters, and save to publish the configuration.`,
              invalidItemCount: checkpointsCountWithInvalidParameters,
              invalidItemLabel: 'checkpoint',
              isDeleting: false,
              isDraft: latestConfiguration.isDraft,
              isSaving: false,
              onCancel: handleCancelConfiguration,
              onSave: handleSaveConfiguration,
              publishedMessage: `Configuration for ${selectedSite.siteId} is published and will be used for the next Site Compliance evaluation. Click on Configure to stage changes.`,
              totalConfigurations: siteConfigurations.length,
            }
          : null,
    };
  };
